import { useMemo, useState } from 'react';
import { useAorText } from 'utils/aorTerminology';
import { toast } from 'react-toastify';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  keys,
  useDuplicateSitrepAsDraftV2,
} from 'components/DisasterGPT/hooks/useSitrepsV2';
import Ics204VersionStatusBar from 'components/IAP/planningView/widgets/AssignmentList/Ics204VersionStatusBar';
import Ics204VersionsDialog, {
  type WalaVersionRow,
} from 'components/IAP/planningView/widgets/AssignmentList/Ics204VersionsDialog';
import type {
  SignatureCompletion,
  WalaSignature,
} from 'components/IAP/planningView/widgets/AssignmentList/ics204SignatureSlots';
import {
  useAorReportApprovalPolicy,
  usePublishAorReport,
} from 'components/Reports/hooks/useAorReportApprovalPolicy';
import RequestApprovalConfirmationModal from 'components/Reports/RequestApprovalConfirmationModal';
import RequestApprovalSuccessModal from 'components/Reports/RequestApprovalSuccessModal';
import type { GetSitrepsResponse } from 'components/Reports/reports.types';
import { publishedAtMs } from 'components/Reports/utils/partitionPublishedSitreps';
import { isApprovedSitrep } from 'components/Reports/utils/reportChains';
import { ITEM_TYPES } from 'constants/itemType';
import { useGetPositionsForIncidentId } from 'hooks/incident/useRoster';
import {
  useEligibleApprovalReviewers,
  useRequestApprovals,
} from 'hooks/useApprovals';

const APPROVED_ROLE = 'Approved';

const POSITION_NAMES_FOR_INCIDENT_REPORT_APPROVAL = [
  'Situation Unit Leader',
  'Planning Section Chief',
  'Incident Commander',
];

const reportVersionCompletion = (
  signatures: WalaSignature[]
): SignatureCompletion => {
  const approved = signatures.some(
    (signature) => signature.role === APPROVED_ROLE
  );
  return {
    status: approved ? 'signed' : 'draft',
    completedCount: approved ? 1 : 0,
    requiredTotal: 1,
    percent: approved ? 100 : 0,
    slots: [],
  };
};

const formatReportVersionStatus = (status: SignatureCompletion['status']) =>
  status === 'signed' ? 'approved' : 'draft';

const toReportVersionRow = (sitrep: GetSitrepsResponse): WalaVersionRow => {
  const approved = isApprovedSitrep(sitrep);
  return {
    id: sitrep.id,
    updatedAt: sitrep.updated_at || sitrep.approved_at || sitrep.timestamp,
    updatedByUserName:
      sitrep.updated_by_user_name || sitrep.created_by_user_name,
    signatures: approved
      ? [
          {
            name: sitrep.approvals?.[0]?.approved_by_user_name || 'Approved',
            role: APPROVED_ROLE,
            signedAt: publishedAtMs(sitrep),
          },
        ]
      : [],
    status: sitrep.approval_status ?? 'APPROVED',
  };
};

type ReportVersionBarProps = {
  versions: GetSitrepsResponse[];
  tip: GetSitrepsResponse;
  inProgress: GetSitrepsResponse | null;
  viewingId: string | null;
  onViewVersion: (id: string | null) => void;
};

const ReportVersionBar = ({
  versions,
  tip,
  inProgress,
  viewingId,
  onViewVersion,
}: ReportVersionBarProps) => {
  const aorText = useAorText();
  const [dialog, setDialog] = useState<'all' | 'signed' | null>(null);
  const [showApprovalConfirm, setShowApprovalConfirm] = useState(false);
  const [showPublishConfirm, setShowPublishConfirm] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [pendingSuccess, setPendingSuccess] = useState(false);

  const duplicate = useDuplicateSitrepAsDraftV2();
  const publishReport = usePublishAorReport();
  const requestApprovals = useRequestApprovals(keys.base, {
    suppressSuccessToast: true,
    deferQueryInvalidation: true,
  });

  const canApprove =
    tip.approval_status === 'DRAFT' || tip.approval_status === 'REJECTED';
  const isAorReport = !!tip.aor_id && !tip.incident_id;
  const aorEligibleReviewersQuery = useEligibleApprovalReviewers({
    itemId: tip.item_id,
    enabled: canApprove && !tip.incident_id,
  });
  const incidentPositionsQuery = useGetPositionsForIncidentId(
    canApprove ? tip.incident_id || '' : ''
  );
  const approvalPolicyQuery = useAorReportApprovalPolicy(
    canApprove && isAorReport ? tip.aor_id : null
  );
  const policySteps = approvalPolicyQuery.data?.resolvedSteps ?? [];
  const publishesDirectly = tip.incident_id
    ? true
    : isAorReport && approvalPolicyQuery.isSuccess && policySteps.length === 0;
  const filteredAorReviewers = aorEligibleReviewersQuery.data ?? [];

  const rows = useMemo(() => versions.map(toReportVersionRow), [versions]);
  const approvedCount = useMemo(
    () => versions.filter(isApprovedSitrep).length,
    [versions]
  );
  const displayed = versions.find((sitrep) => sitrep.id === viewingId) ?? tip;
  const displayedRow = toReportVersionRow(displayed);

  const createItems: {
    key: string;
    label: string;
    onSelect: () => void;
    disabled?: boolean;
    tour?: string;
  }[] = [];
  const incidentPublishable =
    !!tip.incident_id &&
    (tip.approval_status === 'DRAFT' ||
      tip.approval_status === 'REJECTED' ||
      tip.approval_status === 'PENDING');
  if (canApprove || incidentPublishable) {
    createItems.push({
      key: 'approved',
      label: publishesDirectly ? 'Publish' : 'Approved version',
      disabled:
        requestApprovals.isPending ||
        publishReport.isPending ||
        (!tip.incident_id && isAorReport && approvalPolicyQuery.isLoading),
      onSelect: () => {
        if (publishesDirectly) {
          setShowPublishConfirm(true);
          return;
        }
        setShowApprovalConfirm(true);
      },
    });
  } else if (!inProgress && isApprovedSitrep(tip)) {
    createItems.push({
      key: 'draft',
      label: 'New draft version',
      tour: 'reports-new-draft-version',
      disabled: duplicate.isPending,
      onSelect: () => {
        duplicate.mutate(
          { sitrepId: tip.id },
          {
            onSuccess: () => toast.success('Draft version created'),
            onError: () => toast.error('Failed to create a draft version'),
          }
        );
      },
    });
  }

  const handleView = (version: WalaVersionRow) => {
    setDialog(null);
    onViewVersion(version.id === tip.id ? null : version.id);
  };

  const handleConfirmPublish = async () => {
    try {
      await publishReport.mutateAsync(tip.id);
      setShowPublishConfirm(false);
      toast.success('Published as the current report');
    } catch (error) {
      const serverMessage = (
        error as { response?: { data?: { error?: unknown } } }
      ).response?.data?.error;
      toast.error(
        typeof serverMessage === 'string'
          ? serverMessage
          : 'Failed to publish report'
      );
    }
  };

  const handleConfirmApproval = async () => {
    let reviewers: { reviewerItemId: string }[];

    if (tip.incident_id) {
      const positions = incidentPositionsQuery.data;
      reviewers = POSITION_NAMES_FOR_INCIDENT_REPORT_APPROVAL.map(
        (positionName) => {
          const position = positions?.find(
            (candidate) =>
              candidate.name === positionName && !candidate.isResourcePosition
          );
          if (!position) return null;
          return { reviewerItemId: position.item_id };
        }
      ).filter(
        (reviewer): reviewer is { reviewerItemId: string } => reviewer !== null
      );
    } else {
      reviewers = filteredAorReviewers.map((reviewer) => ({
        reviewerItemId: reviewer.reviewerItemId,
      }));

      if (reviewers.length === 0 && policySteps.length === 0) {
        toast.info('No eligible reviewers found. Request sent to you only.');
        setPendingSuccess(true);
        setShowApprovalConfirm(false);
        return;
      }
    }

    await requestApprovals.mutateAsync({
      itemId: tip.item_id,
      itemType: ITEM_TYPES.Sitrep,
      reviewers,
      approvalsRequiredInOrder: !!tip.incident_id,
      groupId: tip.group_guid,
      incidentId: tip.incident_id || undefined,
    });
    setPendingSuccess(true);
    setShowApprovalConfirm(false);
  };

  return (
    <>
      <div data-tour="reports-tabs">
        <Ics204VersionStatusBar
          signatures={displayedRow.signatures as WalaSignature[]}
          viewingPast={viewingId != null}
          isCreatingSignedVersion={false}
          historyCount={rows.length}
          signedCount={approvedCount}
          createItems={createItems}
          updatedAt={displayedRow.updatedAt}
          updatedByUserName={displayedRow.updatedByUserName}
          onOpenHistory={() => setDialog('all')}
          onOpenSigned={() => setDialog('signed')}
          onViewLatest={() => onViewVersion(null)}
          onCancelCreateSigned={() => onViewVersion(null)}
          getCompletion={reportVersionCompletion}
          signedMenuLabel="Approved versions"
          hideProgressRing
          lockedMessage="Approved versions are locked from edits."
          embedded
        />
      </div>
      <Ics204VersionsDialog
        open={dialog != null}
        kind={dialog ?? 'all'}
        versions={rows}
        tipId={tip.id}
        onClose={() => setDialog(null)}
        onView={handleView}
        getCompletion={reportVersionCompletion}
        signedTitle="Approved versions"
        description="View a historical snapshot of this report. Viewing past versions is read-only."
        formatStatus={formatReportVersionStatus}
      />
      <Dialog
        open={showPublishConfirm}
        onOpenChange={(open) => {
          if (publishReport.isPending) return;
          setShowPublishConfirm(open);
        }}
      >
        <DialogContent
          className="dark tw:z-[1101] tw:sm:max-w-lg"
          overlayClassName="tw:z-[1100]"
        >
          <DialogHeader>
            <DialogTitle>Publish this draft?</DialogTitle>
            <DialogDescription>
              This draft becomes the current report. No approval is required.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={publishReport.isPending}
              onClick={() => setShowPublishConfirm(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              disabled={publishReport.isPending}
              onClick={() => {
                void handleConfirmPublish();
              }}
            >
              Publish
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <RequestApprovalConfirmationModal
        show={showApprovalConfirm}
        onHide={() => setShowApprovalConfirm(false)}
        onConfirm={handleConfirmApproval}
        isSubmitting={requestApprovals.isPending}
        name={tip.name}
        createdBy={tip.created_by_user_name ?? 'Unknown'}
        createdAt={tip.timestamp}
        editedBy={
          tip.updated_by_user_name ?? tip.created_by_user_name ?? 'Unknown'
        }
        editedAt={tip.updated_at ?? tip.timestamp}
        incidentId={tip.incident_id}
        groupId={tip.group_guid}
        aorReviewers={filteredAorReviewers}
        approvalSteps={policySteps}
        reviewersLoading={isAorReport && approvalPolicyQuery.isLoading}
        positionNames={
          tip.incident_id
            ? POSITION_NAMES_FOR_INCIDENT_REPORT_APPROVAL
            : filteredAorReviewers.map((reviewer) => reviewer.displayName)
        }
        fallbackReviewerLabel={
          tip.incident_id
            ? undefined
            : aorText(
                'No SITREP reviewers with AOR permissions are configured for this report.'
              )
        }
        onExited={() => {
          if (pendingSuccess) {
            setPendingSuccess(false);
            setShowSuccess(true);
          }
        }}
      />
      <RequestApprovalSuccessModal
        show={showSuccess}
        draftName={tip.name}
        editedBy={
          tip.updated_by_user_name ?? tip.created_by_user_name ?? 'Unknown'
        }
        incidentId={tip.incident_id}
        onHide={() => {
          setShowSuccess(false);
          requestApprovals.invalidateQueries();
        }}
      />
    </>
  );
};

export default ReportVersionBar;
